import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

const SPEAKER_COLORS = ['#19d0ef', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

export default function TranscriptView({ segments, partialText, speakerNames = {}, version = 0 }) {
  const theme = useTheme();
  const bottomRef = useRef(null);
  const [animating, setAnimating] = useState(false);
  const prevVersion = useRef(version);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [segments, partialText]);

  // Trigger flash animation when version changes (rediarization complete)
  useEffect(() => {
    if (version > prevVersion.current) {
      prevVersion.current = version;
      setAnimating(true);
      const timer = setTimeout(() => setAnimating(false), 800);
      return () => clearTimeout(timer);
    }
  }, [version]);

  const getSpeakerLabel = (speakerId) => {
    return speakerNames[speakerId] || `Speaker ${speakerId}`;
  };

  return (
    <Box sx={{
      maxHeight: 400,
      overflowY: 'auto',
      p: 2,
      bgcolor: theme.palette.background.default,
      borderRadius: '8px',
      border: `1px solid ${theme.palette.divider}`,
      transition: 'box-shadow 0.4s ease, border-color 0.4s ease',
      ...(animating && {
        boxShadow: `0 0 12px ${theme.palette.primary.main}40`,
        borderColor: theme.palette.primary.main,
      }),
    }}>
      {segments.length === 0 && !partialText && (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', textAlign: 'center' }}>
          Waiting for speech...
        </Typography>
      )}

      {segments.map((seg, i) => (
        <Box
          key={`${version}-${i}`}
          sx={{
            mb: 1,
            animation: animating ? 'fadeIn 0.5s ease forwards' : 'none',
            animationDelay: animating ? `${Math.min(i * 30, 300)}ms` : '0ms',
            opacity: animating ? 0 : 1,
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(4px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: SPEAKER_COLORS[seg.speaker_id % SPEAKER_COLORS.length],
              fontWeight: 600,
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {getSpeakerLabel(seg.speaker_id)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.primary', ml: 0.5 }}>
            {seg.text}
          </Typography>
        </Box>
      ))}

      {partialText && (
        <Box sx={{ mb: 1, opacity: 0.6 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            {partialText}
          </Typography>
        </Box>
      )}

      <div ref={bottomRef} />
    </Box>
  );
}
